import { CAPTCHA_PROVIDER, CAPTCHA_SITE_KEY } from "./config";

/**
 * Optional, drop-in spam protection. Supports:
 *   - Google reCAPTCHA v3  (invisible, token fetched at submit time)
 *   - Cloudflare Turnstile (explicit widget; token stored when the widget solves)
 *
 * When no provider / site key is configured every function is inert and the
 * forms submit without a captcha, matching the server-side no-op. No secret is
 * ever handled in the browser.
 */

type Provider = "turnstile" | "recaptcha" | "";

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

interface TurnstileGlobal {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
    },
  ) => string;
}

function currentProvider(): Provider {
  const p = CAPTCHA_PROVIDER;
  if (p === "turnstile") return "turnstile";
  if (p === "recaptcha" || p === "recaptcha_v3" || p === "recaptcha-v3" || p === "recaptchav3") {
    return "recaptcha";
  }
  return "";
}

export function captchaEnabled(): boolean {
  return currentProvider() !== "" && CAPTCHA_SITE_KEY !== "";
}

let loader: Promise<boolean> | null = null;

function loadScript(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(false);
      return;
    }
    if (document.querySelector(`script[data-captcha="1"][src="${src}"]`)) {
      resolve(true);
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.defer = true;
    el.dataset.captcha = "1";
    el.onload = () => resolve(true);
    el.onerror = () => resolve(false);
    document.head.appendChild(el);
  });
}

/** Loads the provider script once. Returns false when captcha is disabled. */
export function ensureCaptchaLoaded(): Promise<boolean> {
  if (!captchaEnabled()) return Promise.resolve(false);
  if (loader) return loader;
  const url =
    currentProvider() === "turnstile"
      ? "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      : `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(CAPTCHA_SITE_KEY)}`;
  loader = loadScript(url);
  return loader;
}

// --- Turnstile token store -------------------------------------------------
let turnstileToken = "";

export function setTurnstileToken(token: string): void {
  turnstileToken = token;
}

export function getTurnstileGlobal(): TurnstileGlobal | null {
  const w = window as unknown as { turnstile?: TurnstileGlobal };
  return w.turnstile ?? null;
}

/**
 * Resolves the captcha token to attach to a submission, or an empty string when
 * captcha is not configured. Never throws — a load failure degrades to no token
 * so the server can decide (it no-ops when captcha is disabled).
 */
export async function captchaToken(action = "submit"): Promise<string> {
  if (!(await ensureCaptchaLoaded())) return "";

  if (currentProvider() === "turnstile") {
    return turnstileToken;
  }

  const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
  if (!g) return "";

  return new Promise<string>((resolve) => {
    try {
      g.ready(() => {
        g.execute(CAPTCHA_SITE_KEY, { action })
          .then((token) => resolve(token || ""))
          .catch(() => resolve(""));
      });
    } catch {
      resolve("");
    }
  });
}